
import React, { useRef, useState } from 'react'
import { Modal } from '../Common/Modal';
import http from '../../http';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import AuthUser from '../AuthUser';
import { useReactToPrint } from 'react-to-print';
import RefundSlip from '../Common/RefundSlip';

export default function SalesReturnModal({ invoiceId, clearInformation }) {
    const { user } = AuthUser();

    const [invoiceDetails, setInvoiceDetails] = useState([]);
    const [modalIsOpen, setIsOpen] = useState(false);
    const [invoiceData, setInvoiceData] = useState({});
    const [returnList, setReturnList] = useState([]);
    const [printReturnList, setPrintReturnList] = useState([]);
    const refundSlipRef = useRef();
    const handlePrintRefundSlip = useReactToPrint({
        content: () => refundSlipRef.current,
    });

    const openModal = () => {
        http.get(`/view-selected-invoice/${invoiceId}`)
            .then((res) => {
                setInvoiceDetails(res.data?.invoice_details || []);
                setInvoiceData(res.data.data);
                setIsOpen(true);
            })
            .catch((err) => {
                toast.error('Failed to load invoice details');
            });
    };

    const closeModal = () => {
        clearInformation('Return Invoice Canceled');
        setIsOpen(false);
        setReturnList([]);
        setInvoiceDetails([]);
        setInvoiceData({});
    };

    const handleMedicineSelect = (item, e) => {
        if (e.target.checked) {
            setReturnList(prev => [...prev, { ...item }]);
        } else {
            setReturnList(prev => prev.filter(slt => slt.id !== item.id));
        }
    };

    const returnQtyHandler = (item, e) => {
        const existQty = invoiceDetails.find(slt => slt.id === item.id)?.pcs;
        if (Number(e.target.value) > Number(existQty)) {
            toast.error(`Not available more than ${existQty}`);
            return;
        }
        setReturnList(prev =>
            prev.map(pd =>
                pd.id === item.id ? { ...pd, qty: e.target.value } : pd
            )
        );
    };

    const handleSaleReturn = () => {
        if (returnList.length === 0) {
            toast.error('Please select at least one product to return');
            return;
        }
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#69b128',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, return it!'
        }).then((result) => {
            if (result.isConfirmed) {
                const returnData = {
                    reference_invoice_no: invoiceData?.invoice_no,
                    sales_invoice_id: invoiceId,
                    sales_person_id: user?.id,
                    member_id: invoiceData?.member_id ? invoiceData?.member_id : 'null',
                    total_amount: returnTotal,
                    arrData: JSON.stringify(returnList)
                };
                http.post(`process-sales-return`, returnData)
                    .then(res => {
                        toast.success(res.data.message);
                        setPrintReturnList([...returnList]);
                        setTimeout(() => {
                            handlePrintRefundSlip();
                            setIsOpen(false);
                            clearInformation('Return Invoice Done');
                        }, 400);
                    })
                    .catch(() => {
                        toast.error('Failed to process return');
                    });
            }
        });
    };

    const getDiscountPercent = (item) => {
        if (item.cash_drug_discount) return item.cash_drug_discount;
        if (item.card_drug_discount) return item.card_drug_discount;
        if (item.digital_drug_discount) return item.digital_drug_discount;
        return 0;
    };

    const allReturn = returnList.reduce((a, b) => a + parseInt(b.qty || 0), 0);
    const returnTotal = returnList.reduce((sum, b) => {
        const qty = parseFloat(b.qty || 0);
        const price = parseFloat(b.price || 0);
        const disc = parseFloat(b.item_discount_percentage || 0);
        const subtotal = qty * price;
        return sum + (subtotal - (subtotal * disc / 100));
    }, 0);


    const getReturnAmount = (item) => {
        const findReturnItem = returnList.find(slt => slt.id === item.id);
        if (findReturnItem && parseInt(findReturnItem.qty) > 0) {
            const qty = parseInt(findReturnItem.qty);
            const price = parseFloat(item.price);
            const totalPrice = qty * price;
            const discountPercent = parseFloat(item.item_discount_percentage || 0);
            const discountAmount = (totalPrice * discountPercent) / 100;
            return (totalPrice - discountAmount).toFixed(2);
        }
        return 0;
    }

    const getRemainingAmount = (item) => {
        const findReturnItem = returnList.find((slt) => slt.id === item.id);
        const price = parseFloat(item.price);
        const discountPercent = parseFloat(item.item_discount_percentage || 0);

        const originalQty = parseInt(item.qty);
        const returnQty = findReturnItem ? parseInt(findReturnItem.qty) : 0;
        const remainingQty = originalQty - returnQty;

        const totalRemaining = remainingQty * price;
        const discountAmount = (totalRemaining * discountPercent) / 100;

        return (totalRemaining - discountAmount).toFixed(2);
    };

    return (
        <>
            {/* Hidden Refund Slip for printing */}
            <div style={{ display: 'none' }}>
                <RefundSlip
                    ref={refundSlipRef}
                    user={user}
                    invoiceData={invoiceData}
                    returnList={printReturnList}
                    returnTotal={returnTotal}
                />
            </div>

            <button
                style={{ backgroundColor: '#69B128', color: 'white' }}
                onClick={openModal}
                className="btn btn-sm mt-2 me-lg-2 px-4 fw-bold"
            >
                Return
            </button>

            <Modal isOpen={modalIsOpen} onClose={closeModal} size="xl">
                <Modal.Header onClose={closeModal}>
                    <Modal.Title>Sales Return — {invoiceData?.invoice_no || ''}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {/* Invoice Summary */}
                    <div className="row mb-3" style={{ fontSize: '13px' }}>
                        <div className="col-md-4">
                            <p className="mb-1"><strong>Invoice No:</strong> {invoiceData?.invoice_no}</p>
                            <p className="mb-1"><strong>Date:</strong> {invoiceData?.created_at ? new Date(invoiceData.created_at).toLocaleDateString() : 'N/A'}</p>
                            <p className="mb-1"><strong>Created By:</strong> {invoiceData?.created_by || 'N/A'}</p>
                        </div>
                        <div className="col-md-4">
                            <p className="mb-1"><strong>Customer:</strong> {invoiceData?.member_name || 'Walk-in Customer'}</p>
                            <p className="mb-1"><strong>Phone:</strong> {invoiceData?.member_phone || 'N/A'}</p>
                            <p className="mb-1"><strong>Payment:</strong>{' '}
                                <span className={`badge ${invoiceData?.payment_status === 'Paid' ? 'bg-success' : 'bg-warning'}`}>
                                    {invoiceData?.payment_status}
                                </span>
                            </p>
                        </div>
                        <div className="col-md-4">
                            <p className="mb-1"><strong>Sub Total:</strong> ৳{Number(invoiceData?.sub_total || 0).toFixed(2)}</p>
                            <p className="mb-1"><strong>Discount:</strong> ৳{Number(invoiceData?.discount_amount || 0).toFixed(2)} ({invoiceData?.discount_type || 'N/A'})</p>
                            <p className="mb-1"><strong>Special Discount:</strong> ৳{Number(invoiceData?.special_discount || 0).toFixed(2)}</p>
                            <p className="mb-1"><strong>Grand Total:</strong> <strong style={{ color: '#69B128' }}>৳{Number(invoiceData?.grand_total || 0).toFixed(2)}</strong></p>
                            <p className="mb-1"><strong>Paid:</strong> ৳{Number(invoiceData?.paid_amount || 0).toFixed(2)}</p>
                        </div>
                    </div>

                    <hr className="my-2" />

                    {/* Products Table */}
                    <div style={{ width: '100%' }}>
                        <table className="table table-sm table-bordered table-hover mb-0" style={{ fontSize: '13px' }}>
                            <thead style={{ backgroundColor: '#69B128', color: '#fff' }}>
                                <tr>
                                    <th style={{ width: '40px', textAlign: 'center' }}>#</th>
                                    <th>Product Name</th>
                                    {/* <th style={{ textAlign: 'center' }}>Box Type</th> */}
                                    <th style={{ textAlign: 'center' }}>Pkt Size</th>
                                    <th style={{ textAlign: 'center' }}>Box/Leaf</th>
                                    <th style={{ textAlign: 'center' }}>Qty</th>
                                    <th style={{ textAlign: 'center' }}>Return Qty</th>
                                    <th style={{ textAlign: 'center' }}>Return Amount</th>
                                    <th style={{ textAlign: 'center' }}>Remaining Amount</th>
                                    {/* <th style={{ textAlign: 'center' }}>Discount</th> */}
                                    <th style={{ textAlign: 'right' }}>MRP</th>
                                    <th style={{ textAlign: 'center' }}>Disc%</th>
                                    <th style={{ textAlign: 'right' }}>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoiceDetails.length > 0 ? (
                                    invoiceDetails.map((item, i) => {
                                        const isSelected = returnList.find(slt => slt.id === item.id);
                                        const discount = getDiscountPercent(item);
                                        return (
                                            <tr key={item.id} className={isSelected ? 'table-warning' : ''}>
                                                <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                    <input
                                                        type="checkbox"
                                                        onChange={(e) => handleMedicineSelect(item, e)}
                                                        className="form-check-input"
                                                        checked={!!isSelected}
                                                    />
                                                </td>
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    {item.name}
                                                    {item.expire_date && (
                                                        <small className="text-muted d-block">Exp: {item.expire_date}</small>
                                                    )}
                                                </td>
                                                {/* <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                    {item.boxType || 'N/A'}
                                                </td> */}
                                                <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                    {item.pktSize || 'N/A'}
                                                </td>
                                                <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                    {item.noOfBox}
                                                </td>
                                                <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                    {item.qty} Pcs
                                                </td>

                                                <td style={{ textAlign: 'center', verticalAlign: 'middle', width: '90px' }}>
                                                    {isSelected ? (
                                                        <input
                                                            value={isSelected.qty}
                                                            onChange={(e) => returnQtyHandler(item, e)}
                                                            className="form-control form-control-sm text-center"
                                                            type="number"
                                                            min="1"
                                                            max={item.pcs}
                                                        />
                                                    ) : (
                                                        '—'
                                                    )}
                                                </td>
                                                <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                    {getReturnAmount(item)}
                                                </td>
                                                <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                    {getRemainingAmount(item)}
                                                </td>
                                                {/* <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                    {getDiscountPercentage}%
                                                </td> */}
                                                <td style={{ textAlign: 'right', verticalAlign: 'middle' }}>
                                                    ৳{Number(item.price).toFixed(2)}
                                                </td>
                                                <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                    {parseFloat(item.item_discount_percentage || 0).toFixed(2)}%
                                                </td>
                                                <td style={{ textAlign: 'right', verticalAlign: 'middle' }}>
                                                    {(() => {
                                                        const sub = Number(item.price) * Number(item.qty);
                                                        const disc = sub * parseFloat(item.item_discount_percentage || 0) / 100;
                                                        return '৳' + (sub - disc).toFixed(2);
                                                    })()}
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={11} className="text-center text-muted py-3">No products found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Return Summary */}
                    {returnList.length > 0 && (
                        <div className="d-flex justify-content-end mt-2">
                            <div style={{ fontSize: '13px', textAlign: 'right' }}>
                                <p className="mb-1"><strong>Return Items:</strong> {returnList.length}</p>
                                <p className="mb-1"><strong>Return Qty:</strong> {allReturn}</p>
                                <p className="mb-0" style={{ fontSize: '15px' }}>
                                    <strong>Return Amount: </strong>
                                    <strong style={{ color: '#d33' }}>৳{returnTotal.toFixed(2)}</strong>
                                </p>
                            </div>
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <button
                        className="btn btn-sm px-4"
                        style={{ border: '1px solid #ccc' }}
                        onClick={closeModal}
                    >
                        Cancel
                    </button>
                    {Number(invoiceData?.return_status) === 1 ? (
                        <button
                            style={{ backgroundColor: '#999', color: 'white' }}
                            onClick={() => toast.error("Invoice already returned")}
                            className="btn btn-sm px-4 fw-bold"
                            disabled
                        >
                            Already Returned
                        </button>
                    ) : (
                        <button
                            style={{ backgroundColor: '#69B128', color: 'white' }}
                            onClick={handleSaleReturn}
                            className="btn btn-sm px-4 fw-bold"
                            disabled={returnList.length === 0}
                        >
                            Return Selected ({returnList.length})
                        </button>
                    )}
                </Modal.Footer>
            </Modal>
        </>
    );
}
